import { useNotificationBar } from 'context/notificationContext'
import {
    fetchCategorySearchApi,
    useCategoryContent,
} from 'hooks/category-manage'
import { useCollectionContent } from 'hooks/collection'
import { useCallback, useEffect, useState } from 'react'
import { getErrorResponse } from 'services/util'
import CarouselList from './CarouselList'
import AddItemPanel from './AddItemPanel'

export default function CarouselManager({ widget, setWidgetDraftData }) {
    const notification = useNotificationBar()

    const [carousel, setCarousel] = useState<any>([])
    const [widgetTitle, setWidgetTitle] = useState()
    const [isSaving, setIsSaving] = useState(false)

    // Category
    const [categories, setCategories] = useState<any>([])
    const [selectedCategory, setSelectedCategory] = useState<any>(null)
    const [categorySearch, setCategorySearch] = useState('')
    const [isCategoryOpen, setIsCategoryOpen] = useState(false)
    const [categoryLoading, setCategoryLoading] = useState(false)

    // Collection
    const [collections, setCollections] = useState([])
    const [selectedCollection, setSelectedCollection] = useState<any>(null)
    const [collectionSearch, setCollectionSearch] = useState('')
    const [isCollectionOpen, setIsCollectionOpen] = useState(false)
    const [collectionLoading, setCollectionLoading] = useState(false)

    // Show
    const [shows, setShows] = useState<any>([])
    const [selectedShow, setSelectedShow] = useState<any>(null)
    const [showSearch, setShowSearch] = useState('')
    const [isShowOpen, setIsShowOpen] = useState(false)
    const [showLoading, setShowLoading] = useState(false)

    // Item Type & Results
    const [itemType, setItemType] = useState('show')
    const [results, setResults] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [showAddPanel, setShowAddPanel] = useState(false)
    const [selectedItem, setSelectedItem] = useState<any>(null)

    const [categoryContentData, setCategoryContent] = useState<any>(null)

    // Fetch Categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setCategoryLoading(true)
                const response = await fetchCategorySearchApi('')
                const formattedCategories = Array.isArray(response)
                    ? response.map((item) => ({
                          id: item.id,
                          label: item.name,
                          value: item.id,
                          thumbnail: item.chipImageUrl,
                      }))
                    : []
                setCategories(formattedCategories)
            } catch (error) {
                notification.push(getErrorResponse(error))
                setCategories([])
            } finally {
                setCategoryLoading(false)
            }
        }
        if (widget?.widget_data?.widget_items?.length > 0) {
            setCarousel(widget?.widget_data?.widget_items)
        }
        fetchCategories()
    }, [notification])

    // Fetch Collections when Category changes
    const { data: categoryData, isFetching: loadingCategoryContent } =
        useCategoryContent(selectedCategory?.id)

    const { data: showData, isFetching: loadingShowContent } =
        useCollectionContent(selectedCollection?.id)

    useEffect(() => {
        if (!selectedCategory || !categoryData?.data) {
            setCollections([])
            setSelectedCollection(null)
            return
        }

        try {
            setCollectionLoading(true)
            const categoryContent = categoryData?.data?.category
            const collectionOptions = Array.isArray(
                categoryContent?.collections
            )
                ? categoryContent.collections.map((item) => ({
                      id: item.collection?.id,
                      label: item.collection?.name,
                      value: item.collection?.id,
                      thumbnail: item.collection?.thumbnail,
                  }))
                : []
            setCategoryContent(categoryContent)
            setCollections(collectionOptions)
        } catch (error) {
            notification.push(getErrorResponse(error))
            setCollections([])
        } finally {
            setCollectionLoading(false)
        }
    }, [selectedCategory, categoryData?.data, notification])

    // Fetch Shows when Collection changes (for episodes)
    useEffect(() => {
        if (!selectedCollection || itemType !== 'episode') {
            setShows([])
            setSelectedShow(null)
            return
        }
        fetchShows()
    }, [selectedCollection, itemType, notification])

    const fetchShows = async () => {
        try {
            setShowLoading(true)
            const categoryContent = showData?.data?.collection
            console.log('collectiondd', categoryContent)
            const collectionOptions = Array.isArray(categoryContent?.shows)
                ? categoryContent.shows.map((item) => ({
                      id: item.show?.id,
                      label: item.show?.name,
                      value: item.show?.id,
                      thumbnail: item.show?.thumbnail,
                      type: 'show',
                  }))
                : []
            const episodeOptions = Array.isArray(categoryContent?.episodes)
                ? categoryContent.shows.map((item) => ({
                      id: item.episode?.id,
                      label: item.episode?.name,
                      value: item.episode?.id,
                      thumbnail: item.episode?.thumbnail,
                      type: 'episode',
                  }))
                : []

            setShows(collectionOptions)
            return { shows: collectionOptions, episodes: episodeOptions }
        } catch (error) {
            notification.push(getErrorResponse(error))
            setShows([])
        } finally {
            setShowLoading(false)
        }
    }
    const handleItemTypeChange = useCallback((newType) => {
        setItemType(newType)
        setSelectedShow(null)
        setShowSearch('')
        setIsShowOpen(false)
        setResults(null)
        setSelectedItem(null)
    }, [])

    const handleCategorySelect = useCallback((category) => {
        setSelectedCategory(category)
        setIsCategoryOpen(false)
        setCategorySearch('')
        setSelectedCollection(null)
        setResults(null)
        setSelectedItem(null)
    }, [])

    const handleCollectionSelect = useCallback((collection) => {
        setSelectedCollection(collection)
        setIsCollectionOpen(false)
        setCollectionSearch('')
        setResults(null)
        setSelectedItem(null)
    }, [])

    const handleShowSelect = useCallback((show) => {
        setSelectedShow(show)
        setIsShowOpen(false)
        setShowSearch('')
        setResults(null)
        setSelectedItem(null)
    }, [])

    const handleSubmit = useCallback(async () => {
        if (!selectedCategory) {
            notification.push({
                message: 'Please select all required fields',
                type: 'error',
            })
            return
        }

        setLoading(true)
        try {
            let apiUrl: any = []
            const categoryContent = categoryContentData
            if (selectedCategory && !selectedShow && !selectedCollection) {
                const collectionOptions = Array.isArray(categoryContent?.shows)
                    ? categoryContent.shows.map((item) => ({
                          id: item.show?.id,
                          label: item.show?.name,
                          name: item.show.name,
                          value: item.show?.id,
                          thumbnail: item.show?.thumbnail,
                          type: 'show',
                      }))
                    : []
                let episodeOptions = []
                if (itemType === 'episode') {
                 episodeOptions =   Array.isArray(categoryContent?.episodes)
                        ? categoryContent?.episodes.map((item) => ({
                              id: item.episode?.id,
                              label: item.episode?.name,
                              value: item.episode?.id,
                              thumbnail: item.episode?.thumbnail,
                              type: 'episode',
                          }))
                        : []
                }
                // }
                apiUrl = { shows: collectionOptions, episodes: episodeOptions }
            } else if (
                selectedCategory &&
                selectedCollection &&
                !selectedShow
            ) {
                const collectionOptions = Array.isArray(categoryContent?.shows)
                    ? categoryContent.shows.map((item) => ({
                          id: item.show?.id,
                          label: item.show?.name,
                          name: item.show.name,
                          value: item.show?.id,
                          thumbnail: item.show?.thumbnail,
                          type: 'show',
                      }))
                    : []
                let episodeOptions = []
                if (itemType === 'episode') {
                 episodeOptions =    Array.isArray(categoryContent?.episodes)
                        ? categoryContent?.episodes.map((item) => ({
                              id: item.episode?.id,
                              label: item.episode?.name,
                              value: item.episode?.id,
                              thumbnail: item.episode?.thumbnail,
                              type: 'episode',
                          }))
                        : []
                }
                // }
                apiUrl = { shows: collectionOptions, episodes: episodeOptions }
            } else if (selectedCategory && selectedCollection && selectedShow) {
                const collectionOptions = Array.isArray(categoryContent?.shows)
                    ? categoryContent.shows.map((item) => ({
                          id: item.show?.id,
                          label: item.show?.name,
                          name: item.show.name,
                          value: item.show?.id,
                          thumbnail: item.show?.thumbnail,
                          type: 'show',
                      }))
                    : []
                let episodeOptions = []
                if (itemType === 'episode') {
                   episodeOptions =   Array.isArray(categoryContent?.episodes)
                        ? categoryContent?.episodes.map((item) => ({
                              id: item.episode?.id,
                              label: item.episode?.name,
                              value: item.episode?.id,
                              thumbnail: item.episode?.thumbnail,
                              type: 'episode',
                          }))
                        : []
                }
                // }
                apiUrl = { shows: collectionOptions, episodes: episodeOptions }
            } else {
                apiUrl = { shows: [], episodes: [] }
            }

            // else {
            //   apiUrl = `/api/episodes/${selectedShow.id}`;
            // }

            const formattedItems = apiUrl?.shows
                ? apiUrl?.shows.map((item) => ({
                      id: item.id,
                      name: item.name,
                      label: item.name,
                      type: 'show',
                      thumbnail: item.thumbnail,
                  }))
                : []
            console.log('formattedItems', formattedItems)
            const episodeItems = apiUrl?.episodes
                ? apiUrl?.episodes.map((item) => ({
                      id: item.id,
                      name: item.name,
                      label: item.name,
                      type: 'episode',
                      thumbnail: item.thumbnail,
                  }))
                : []

            const finalItems = [...formattedItems, ...episodeItems]
            console.log('finalItems', finalItems)
            setResults({
                itemType: itemType,
                category: selectedCategory,
                collection: selectedCollection,
                show: selectedShow,
                items: finalItems,
            })
        } catch (error) {
            notification.push(getErrorResponse(error))
            setResults(null)
        } finally {
            setLoading(false)
        }
    }, [
        itemType,
        selectedCategory,
        selectedCollection,
        selectedShow,
        notification,
    ])

    const moveItem = useCallback((index, direction) => {
        setCarousel((prev) => {
            const newCarousel = [...prev]
            if (direction === 'up' && index > 0) {
                ;[newCarousel[index], newCarousel[index - 1]] = [
                    newCarousel[index - 1],
                    newCarousel[index],
                ]
            } else if (direction === 'down' && index < prev.length - 1) {
                ;[newCarousel[index], newCarousel[index + 1]] = [
                    newCarousel[index + 1],
                    newCarousel[index],
                ]
            }
            return newCarousel
        })
    }, [])

    const deleteItem = useCallback((index) => {
        setCarousel((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const addItemToCarousel = useCallback(() => {
        if (!selectedItem) {
            notification.push({
                message: 'Please select an item',
                type: 'error',
            })
            return
        }

        const redirectionType = itemType === 'show' ? 'SHOW' : 'EPISODE'

        setCarousel((prev: any[]) => [
            ...prev,
            {
                redirection_id: selectedItem.id,
                title: selectedItem.name || selectedItem.label,
                redirection_to: redirectionType,
                thumbnail: selectedItem.thumbnail || '',
            },
        ])

        resetAddPanel()
    }, [selectedItem, itemType, notification])

    const resetAddPanel = useCallback(() => {
        setSelectedItem(null)
        setShowAddPanel(false)
        setResults(null)
        setSelectedCategory(null)
        setSelectedCollection(null)
        setSelectedShow(null)
        setCategorySearch('')
        setCollectionSearch('')
        setShowSearch('')
        setIsCategoryOpen(false)
        setIsCollectionOpen(false)
        setIsShowOpen(false)
    }, [])

    const handleSaveCarousel = useCallback(async () => {
        if (carousel?.length === 0) {
            notification.push({
                message: 'Please add at least one item',
                type: 'error',
            })
            return
        }

        setIsSaving(true)
        try {
            const widgetData = {
                ...widget,
                widget_data: {
                    ...widget?.widget_data,
                    widget_title: widgetTitle ? widgetTitle : '',
                    widget_icon: '',
                    view_all: true,
                    redirection_to: '',
                    widget_items: carousel?.map((item) => ({
                        title: item.title,
                        thumbnail: item.thumbnail,
                        redirection_to: item.redirection_to,
                        redirection_id: item.redirection_id,
                    })),
                },
            }

            setWidgetDraftData(widgetData)

            notification.push({
                message: 'Carousel saved successfully!',
                type: 'success',
            })
            console.log('Final Widget Data:', widgetData)
        } catch (error) {
            notification.push(getErrorResponse(error))
        } finally {
            setIsSaving(false)
        }
    }, [carousel, widgetTitle, setWidgetDraftData, notification])

    return (
        <div className="bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto grid grid-cols-2 gap-8">
                <CarouselList
                    items={carousel}
                    onMoveUp={(idx) => moveItem(idx, 'up')}
                    onMoveDown={(idx) => moveItem(idx, 'down')}
                    onDelete={deleteItem}
                    onAddClick={() => setShowAddPanel(true)}
                    onSave={handleSaveCarousel}
                    isSaving={isSaving}
                    widgetTitle={widgetTitle}
                    onWidgetTitleChange={setWidgetTitle}
                />

                <div className="space-y-4">
                    {showAddPanel && (
                        <AddItemPanel
                            itemType={itemType}
                            onItemTypeChange={handleItemTypeChange}
                            selectedCategory={selectedCategory}
                            onCategorySelect={handleCategorySelect}
                            categorySearch={categorySearch}
                            onCategorySearchChange={setCategorySearch}
                            isCategoryOpen={isCategoryOpen}
                            onCategoryToggle={() =>
                                setIsCategoryOpen(!isCategoryOpen)
                            }
                            categories={categories}
                            categoryLoading={categoryLoading}
                            selectedCollection={selectedCollection}
                            onCollectionSelect={handleCollectionSelect}
                            collectionSearch={collectionSearch}
                            onCollectionSearchChange={setCollectionSearch}
                            isCollectionOpen={isCollectionOpen}
                            onCollectionToggle={() =>
                                setIsCollectionOpen(!isCollectionOpen)
                            }
                            collections={collections}
                            collectionLoading={
                                collectionLoading || loadingCategoryContent
                            }
                            selectedShow={selectedShow}
                            onShowSelect={handleShowSelect}
                            showSearch={showSearch}
                            onShowSearchChange={setShowSearch}
                            isShowOpen={isShowOpen}
                            onShowToggle={() => setIsShowOpen(!isShowOpen)}
                            shows={shows}
                            showLoading={showLoading}
                            onShow={handleSubmit}
                            isLoading={loading}
                            results={results}
                            selectedItem={selectedItem}
                            onSelectItem={setSelectedItem}
                            onAddItem={addItemToCarousel}
                            onClose={resetAddPanel}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
